
import { tableStyles } from "./app-files-styles.js";
import type { FileItem } from "./app-files-support.js";
import { IconEllipsisVertical } from "./icons/ellipsis-vertical.js";
import { IconFile } from "./icons/file.js";
import { IconFolder } from "./icons/folder.js";

function parseRows(rows: unknown): FileItem[] {
  return Array.isArray(rows) ? rows as FileItem[] : JSON.parse(String(rows || "[]")) as FileItem[];
}

export class AppDataTable extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppDataTable)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["rows","data-rows","selected","data-selected","selectable","data-selectable","loading","data-loading","loadingtext","data-loading-text","emptytext","data-empty-text"];

  #rows = "[]";
  #selected = [];
  #selectable = true;
  #loading = false;
  #loadingText = "Loading...";
  #emptyText = "No results.";
  #rowsSet = false;
  #selectedSet = false;
  #selectableSet = false;
  #loadingSet = false;
  #loadingTextSet = false;
  #emptyTextSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #hooksLive = false;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get rows() {
    return this.#rows;
  }

  set rows(value) {
    const raw = value ?? "[]";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#rows, next)) return;
    this.#rows = next;
    this.#rowsSet = true;
    this.#updateBindings('rows');
  }

  get selected() {
    return this.#selected;
  }

  set selected(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#selected, next)) return;
    this.#selected = next;
    this.#selectedSet = true;
    this.#updateBindings('selected');
  }

  get selectable() {
    return this.#selectable;
  }

  set selectable(value) {
    const raw = value ?? true;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#selectable, next)) return;
    this.#selectable = next;
    this.#selectableSet = true;
    this.#updateBindings('selectable');
  }

  get loading() {
    return this.#loading;
  }

  set loading(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#loading, next)) return;
    this.#loading = next;
    this.#loadingSet = true;
    this.#updateBindings('loading');
  }

  get loadingText() {
    return this.#loadingText;
  }

  set loadingText(value) {
    const raw = value ?? "Loading...";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#loadingText, next)) return;
    this.#loadingText = next;
    this.#loadingTextSet = true;
    this.#updateBindings('loadingText');
  }

  get emptyText() {
    return this.#emptyText;
  }

  set emptyText(value) {
    const raw = value ?? "No results.";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#emptyText, next)) return;
    this.#emptyText = next;
    this.#emptyTextSet = true;
    this.#updateBindings('emptyText');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-rows')) this.rows = this.dataset.rows;
    if (this.hasAttribute('data-selected')) this.selected = this.dataset.selected;
    if (this.hasAttribute('data-selectable')) this.selectable = this.dataset.selectable;
    if (this.hasAttribute('data-loading')) this.loading = this.dataset.loading;
    if (this.hasAttribute('data-loading-text')) this.loadingText = this.dataset.loadingText;
    if (this.hasAttribute('data-empty-text')) this.emptyText = this.dataset.emptyText;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.rows = props.rows;
      this.selected = props.selected;
      this.selectable = props.selectable;
      this.loading = props.loading;
      this.loadingText = props.loadingText;
      this.emptyText = props.emptyText;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#rowsSet) this.rows = this.dataset.rows;
    if (!this.#selectedSet) this.selected = this.dataset.selected;
    if (!this.#selectableSet) this.selectable = this.dataset.selectable;
    if (!this.#loadingSet) this.loading = this.dataset.loading;
    if (!this.#loadingTextSet) this.loadingText = this.dataset.loadingText;
    if (!this.#emptyTextSet) this.emptyText = this.dataset.emptyText;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'rows':
      case 'data-rows':
        this.rows = newValue;
        break;
      case 'selected':
      case 'data-selected':
        this.selected = newValue;
        break;
      case 'selectable':
      case 'data-selectable':
        this.selectable = newValue;
        break;
      case 'loading':
      case 'data-loading':
        this.loading = newValue;
        break;
      case 'loadingtext':
      case 'data-loading-text':
        this.loadingText = newValue;
        break;
      case 'emptytext':
      case 'data-empty-text':
        this.emptyText = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    const rowItems = parseRows(this.#rows);
    return `<style>${tableStyles}</style><div class="table-root" data-argon="1548"><!--argon-text:0-->${(rowItems.length === 0) ? `<div class="empty">${this.#escText(this.#loading ? this.#loadingText : this.#emptyText)}</div>` : `<div class="table-wrap"><table><thead><tr>${this.#selectable ? "<th class=\"select\"><input type=\"checkbox\" aria-label=\"Select all rows\" data-select=\"all\"></th>" : ""}<th>Name</th><th class="col-size">Size</th><th class="col-updated">Updated</th><th class="col-actions" aria-label="Actions"></th></tr></thead><tbody>${rowItems.map((row) => `<tr>${this.#selectable ? `<td class="select"><input type="checkbox" aria-label="Select row" data-row-id="${row.path}"></td>` : ""}<td><button class="cell-action" type="button" data-row-action="open" data-row-id="${row.path}"><span class="cell-icon">${(row.kind === "directory") ? `<icon-folder>${""}</icon-folder>` : `<icon-file>${""}</icon-file>`}</span><span>${row.name}</span></button></td><td class="col-size">${row.sizeLabel}</td><td class="col-updated">${row.updatedLabel}</td><td class="col-actions">${(row.kind === "file") ? `<button class="row-menu" type="button" aria-label="${this.#escAttr(`Actions for ${row.name}`)}" data-row-action="menu" data-row-id="${row.path}"><icon-ellipsis-vertical>${""}</icon-ellipsis-vertical></button>` : this.#escText("")}</td></tr>`).join("")}</tbody></table></div>`}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const currentRows = parseRows(this.#rows);
    const picked = new Set(this.#selected);
    const root = this.shadowRoot!;
    for (const box of root.querySelectorAll<HTMLInputElement>("input[data-row-id]")) {
        box.checked = picked.has(box.dataset.rowId!);
    }
    const all = root.querySelector<HTMLInputElement>('input[data-select="all"]');
    if (all)
        all.checked = currentRows.length > 0 && currentRows.every((row) => picked.has(row.path));
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const rowItems = parseRows(this.#rows);
    if (prop === undefined || prop === 'rows' || prop === 'loading' || prop === 'loadingText' || prop === 'emptyText' || prop === 'selectable') {
      const __argonNext = `${(rowItems.length === 0) ? `<div class="empty">${this.#escText(this.#loading ? this.#loadingText : this.#emptyText)}</div>` : `<div class="table-wrap"><table><thead><tr>${this.#selectable ? "<th class=\"select\"><input type=\"checkbox\" aria-label=\"Select all rows\" data-select=\"all\"></th>" : ""}<th>Name</th><th class="col-size">Size</th><th class="col-updated">Updated</th><th class="col-actions" aria-label="Actions"></th></tr></thead><tbody>${rowItems.map((row) => `<tr>${this.#selectable ? `<td class="select"><input type="checkbox" aria-label="Select row" data-row-id="${row.path}"></td>` : ""}<td><button class="cell-action" type="button" data-row-action="open" data-row-id="${row.path}"><span class="cell-icon">${(row.kind === "directory") ? `<icon-folder>${""}</icon-folder>` : `<icon-file>${""}</icon-file>`}</span><span>${row.name}</span></button></td><td class="col-size">${row.sizeLabel}</td><td class="col-updated">${row.updatedLabel}</td><td class="col-actions">${(row.kind === "file") ? `<button class="row-menu" type="button" aria-label="${this.#escAttr(`Actions for ${row.name}`)}" data-row-action="menu" data-row-id="${row.path}"><icon-ellipsis-vertical>${""}</icon-ellipsis-vertical></button>` : this.#escText("")}</td></tr>`).join("")}</tbody></table></div>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'rows' || prop === 'selected')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '1548').forEach(el => this.#listen(el, '1548', 'change', (event: Event) => {
    const box = event.target as HTMLInputElement;
    if (box.type !== "checkbox")
        return;
    let next: string[];
    if (box.dataset.select === "all") {
        const currentRows = parseRows(this.#rows);
        next = box.checked ? currentRows.map((row) => row.path) : [];
    }
    else {
        next = this.#selected.filter((id) => id !== box.dataset.rowId);
        if (box.checked)
            next.push(box.dataset.rowId!);
    }
    this.dispatchEvent(new CustomEvent("selection-change", { bubbles: true, composed: true, detail: { selectedIds: next } }));
}));
    this.#eventTargets(scope, '1548').forEach(el => this.#listen(el, '1548', 'click', (event: Event) => {
    const action = (event.target as Element).closest<HTMLElement>("[data-row-action]");
    if (!action)
        return;
    this.dispatchEvent(new CustomEvent("row-action", { bubbles: true, composed: true, detail: {
            action: action.dataset.rowAction ?? "",
            rowId: action.dataset.rowId ?? "",
            left: action.getBoundingClientRect().right,
            top: action.getBoundingClientRect().bottom,
        } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
